import { dimAuto, dimPercent, dimPx, normalizeDimValue, type DimValue } from '../../scene/sceneState.js';
import type { CommandBatch } from '../../editor/commandHistory.js';
import { createDragger, type DragFieldElement, type DragFieldVisualOptions } from '../controls/dragField.js';
import {
  getInspectorFieldLabel,
  getInspectorSectionLabel,
  getInspectorSubsectionLabel,
  type InspectorFieldId,
  type InspectorSectionId,
  type InspectorSubsectionId,
} from './inspectorLabels.js';

export interface SceneTreeWidgetDeps {
  commandBatch: CommandBatch;
}

export interface NumberTupleComponent {
  index: number;
  title: string;
  min?: number;
  max?: number;
  step?: number;
  visual?: DragFieldVisualOptions;
}

export interface NumberTupleInputOptions {
  min: number;
  max: number;
  step?: number;
  enabled?: boolean;
  historyLabel?: string;
  visual?: DragFieldVisualOptions;
}

export function createSectionHeader(sectionId: InspectorSectionId): HTMLElement {
  const label = getInspectorSectionLabel(sectionId);
  const el = document.createElement('div');
  el.className = 'scene-props-section';
  el.dataset.inspectorSection = sectionId;
  el.textContent = label;
  return el;
}

export function createSubsectionGroup(subsectionId: InspectorSubsectionId): HTMLElement {
  const label = getInspectorSubsectionLabel(subsectionId);
  const el = document.createElement('div');
  el.className = 'scene-props-subsection-group';
  el.dataset.inspectorSubsection = subsectionId;
  const labelEl = document.createElement('span');
  labelEl.className = 'scene-props-subsection-label';
  labelEl.textContent = label;
  labelEl.title = label;
  el.appendChild(labelEl);
  return el;
}

export function createPropRow(fieldId: InspectorFieldId): HTMLElement {
  const label = getInspectorFieldLabel(fieldId);
  const row = document.createElement('div');
  row.className = 'scene-prop-row';
  row.dataset.inspectorField = fieldId;
  const lbl = document.createElement('label');
  lbl.className = 'scene-prop-label';
  lbl.textContent = label;
  lbl.title = label;
  row.appendChild(lbl);
  return row;
}

export function createCheckboxField(input: HTMLInputElement): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'scene-prop-checkbox';
  wrap.appendChild(input);
  return wrap;
}

export function createNumberInput(
  deps: SceneTreeWidgetDeps,
  value: number,
  min: number,
  max: number,
  onChange: (v: number) => void,
  step = 1,
  historyLabel?: string,
  visual?: DragFieldVisualOptions,
): HTMLElement {
  const history = historyLabel
    ? {
        onBegin: () => deps.commandBatch.begin(historyLabel),
        onEnd: (changed: boolean) => { if (changed) deps.commandBatch.commit(); else deps.commandBatch.discard(); },
      }
    : undefined;
  const d = createDragger(value, min, max, step, onChange, history, visual);
  d.style.flex = '1';
  return d;
}

export function createNumberTupleInput<T extends number[]>(
  deps: SceneTreeWidgetDeps,
  value: T,
  components: readonly NumberTupleComponent[],
  onChange: (v: T) => void,
  options: NumberTupleInputOptions,
): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'scene-prop-vector';
  wrap.style.setProperty('--scene-prop-vector-count', String(Math.max(1, components.length)));
  let current = value.map(entry => Number.isFinite(entry) ? entry : 0) as T;
  const enabled = options.enabled ?? true;
  const defaultStep = options.step ?? 1;

  for (const component of components) {
    const input = createNumberInput(
      deps,
      current[component.index] ?? 0,
      component.min ?? options.min,
      component.max ?? options.max,
      enabled ? next => {
        const nextValue = current.slice() as number[];
        nextValue[component.index] = next;
        current = nextValue as T;
        onChange(current);
      } : () => {},
      component.step ?? defaultStep,
      enabled && options.historyLabel ? `${options.historyLabel} ${component.title.toLowerCase()}` : undefined,
      component.visual ?? options.visual,
    );
    input.title = component.title;
    if (!enabled) input.classList.add('drag-field--disabled');
    wrap.appendChild(input);
  }

  return wrap;
}

export function createNullableNumberInput(
  deps: SceneTreeWidgetDeps,
  value: DimValue | number | null | undefined,
  onChange: (v: DimValue) => void,
  defaultNumeric = 100,
  historyLabel?: string,
): HTMLElement {
  const outer = document.createElement('div');
  outer.style.cssText = 'display:flex;align-items:center;gap:4px;flex:1;min-width:0';

  const currentDim = normalizeDimValue(value);
  let currentNum = currentDim.kind === 'auto' ? defaultNumeric : currentDim.value;
  let currentKind: DimValue['kind'] = currentDim.kind;
  const history = historyLabel
    ? {
        onBegin: () => deps.commandBatch.begin(historyLabel),
        onEnd: (changed: boolean) => { if (changed) deps.commandBatch.commit(); else deps.commandBatch.discard(); },
      }
    : undefined;

  const visualForDimKind = (): DragFieldVisualOptions => currentKind === 'percent'
    ? { subtype: 'percentage', unit: 'percent' }
    : { subtype: 'pixels' };
  const createDimensionDragger = (): DragFieldElement => {
    const next = createDragger(currentNum, 0, 9999, 1, v => {
      currentNum = v;
      if (currentKind === 'px') onChange(dimPx(v));
      else if (currentKind === 'percent') onChange(dimPercent(v));
    }, history, visualForDimKind());
    next.style.flex = '1';
    next.style.minWidth = '0';
    if (currentKind === 'auto') next.classList.add('drag-field--disabled');
    return next;
  };
  let dragger = createDimensionDragger();
  const remountDragger = (): void => {
    const next = createDimensionDragger();
    dragger.replaceWith(next);
    dragger = next;
  };

  const modeSelect = document.createElement('select');
  modeSelect.className = 'scene-prop-input';
  modeSelect.style.cssText = 'flex:0 0 62px;min-width:62px';
  for (const [kind, label] of [['auto', 'auto'], ['px', 'px'], ['percent', '%']] as const) {
    const opt = document.createElement('option');
    opt.value = kind;
    opt.textContent = label;
    if (currentKind === kind) opt.selected = true;
    modeSelect.appendChild(opt);
  }

  modeSelect.addEventListener('change', () => {
    if (historyLabel) deps.commandBatch.begin(historyLabel);
    currentKind = modeSelect.value as DimValue['kind'];
    remountDragger();
    if (currentKind === 'auto') {
      onChange(dimAuto());
    } else if (currentKind === 'px') {
      onChange(dimPx(currentNum));
    } else {
      onChange(dimPercent(currentNum));
    }
    if (historyLabel) deps.commandBatch.commit();
  });

  outer.appendChild(dragger);
  outer.appendChild(modeSelect);
  return outer;
}

export function formatIntrinsicSizeValue(value: number): string {
  return value.toFixed(2).replace(/\.?0+$/, '');
}

export function createReadonlyDimensionValue(value: number): HTMLElement {
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'scene-prop-input scene-prop-input--readonly';
  input.readOnly = true;
  input.value = formatIntrinsicSizeValue(value);
  return input;
}

export function createReadonlyDimensionInput(value: number, unit = 'px', intrinsicDim?: string): HTMLElement {
  const outer = document.createElement('div');
  outer.style.cssText = 'display:flex;align-items:center;gap:4px;flex:1;min-width:0';
  const valueInput = createReadonlyDimensionValue(value);
  if (intrinsicDim) valueInput.dataset.intrinsicDim = intrinsicDim;
  valueInput.style.flex = '1';
  valueInput.style.minWidth = '0';

  const unitSelect = document.createElement('select');
  unitSelect.className = 'scene-prop-input';
  unitSelect.disabled = true;
  unitSelect.style.cssText = 'flex:0 0 62px;min-width:62px';
  const opt = document.createElement('option');
  opt.value = unit;
  opt.textContent = unit;
  opt.selected = true;
  unitSelect.appendChild(opt);

  outer.appendChild(valueInput);
  outer.appendChild(unitSelect);
  return outer;
}
